<script setup>
import { onBeforeUnmount, onMounted, watch, ref } from "vue";
import Choices from "choices.js";
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";

const emit = defineEmits(["update:modelValue", "selectedChange"]);
const props = defineProps({
  size: {
    type: String,
    default: "default",
  },
  success: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  modelValue: {
    type: [Array, String, Number],
    default: "",
  },
  options: {
    type: Array,
    default() {
      return [];
    },
  },
  placeholder: {
    type: String,
    default: "",
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  nameValue: {
    type: String,
    default: "value",
  },
  nameLabel: {
    type: String,
    default: "label",
  },
  choiceLabel: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "description",
  },
  filterable: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  menuWidth: {
    type: String,
    default: "300",
  },
  menuHeight: {
    type: String,
    default: "",
  },
  selectedTooltip: {
    type: Boolean,
    default: false,
  },
  selectedTooltipField: {
    type: String,
    default: "",
  },
  selectedItemSize: {
    type: String,
    default: "sm",
  }
});

let localValue = ref(props.modelValue);
const fileFormatChoices = ref(null);
const isSyncingFromProps = ref(false);
const optionsSignature = ref("");
const selectedTooltipCleanupHandler = ref(null);
const selectedTooltipResyncHandler = ref(null);
const placeholderRestoreHandler = ref(null);

function isEmptyValue(value) {
  return value === null || value === undefined || value === "";
}

function toComparableValue(value) {
  return isEmptyValue(value) ? "" : String(value);
}

function escapeHtmlAttribute(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function areValuesEqual(left, right) {
  const leftIsArray = Array.isArray(left);
  const rightIsArray = Array.isArray(right);

  if (leftIsArray || rightIsArray) {
    const leftValues = leftIsArray ? left : isEmptyValue(left) ? [] : [left];
    const rightValues = rightIsArray ? right : isEmptyValue(right) ? [] : [right];
    if (leftValues.length !== rightValues.length) return false;

    return leftValues.every((value, index) => toComparableValue(value) === toComparableValue(rightValues[index]));
  }

  return toComparableValue(left) === toComparableValue(right);
}

function buildOptionsSignature(options = []) {
  const labelKeys = props.nameLabel
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
  const choiceLabelKeys = props.choiceLabel
    ? props.choiceLabel.split(",").map((label) => label.trim()).filter(Boolean)
    : [];

  return options
    .map((option) => {
      const value = toComparableValue(option?.[props.nameValue]);
      const labels = labelKeys.map((labelKey) => toComparableValue(option?.[labelKey])).join(" - ");
      const choiceLabels = choiceLabelKeys.map((labelKey) => toComparableValue(option?.[labelKey])).join(" - ");
      const description = toComparableValue(option?.[props.description]);
      const image = toComparableValue(option?.image);
      const icon = toComparableValue(option?.icon);
      const selectedTooltip = toComparableValue(
        props.selectedTooltipField ? option?.[props.selectedTooltipField] : ""
      );
      return `${value}::${labels}::${choiceLabels}::${description}::${image}::${icon}::${selectedTooltip}`;
    })
    .join("||");
}

function startProgrammaticSync() {
  isSyncingFromProps.value = true;
  Promise.resolve().then(() => {
    isSyncingFromProps.value = false;
  });
}

function formatOptions(options = []) {
  const listLabel = props.nameLabel.split(",");
  const listChoiceLabel = props.choiceLabel ? props.choiceLabel.split(",") : null;
  return options.map((option) => ({
    value: option?.[props.nameValue],
    label: listLabel.map((label) => option?.[label]).join(" - "),
    customProperties: {
      description: option?.[props.description],
      image: option?.image,
      icon: option?.icon || "",
      selectedTooltip: props.selectedTooltipField ? option?.[props.selectedTooltipField] : "",
      choiceLabel: listChoiceLabel ? listChoiceLabel.map((label) => option?.[label]).join(" - ") : "",
    },
  }));
}

function hasOptionValue(value, options = []) {
  return options.some((option) => option?.[props.nameValue] == value);
}

function getNormalizedValue(modelValue, options = []) {
  if (props.multiple) {
    const values = Array.isArray(modelValue)
      ? modelValue
      : isEmptyValue(modelValue)
        ? []
        : [modelValue];

    let hasInvalidValues = false;
    for (const value of values) {
      if (!hasOptionValue(value, options)) {
        hasInvalidValues = true;
        break;
      }
    }

    if (!hasInvalidValues) return values;
    return values.filter((value) => hasOptionValue(value, options));
  }

  if (isEmptyValue(modelValue)) return "";
  return hasOptionValue(modelValue, options) ? modelValue : "";
}

function setPlaceholderItem() {
  if (props.multiple || !props.placeholder || !fileFormatChoices.value) return;

  fileFormatChoices.value._addItem({
    value: "",
    label: props.placeholder,
    choiceId: -1,
    groupId: -1,
    placeholder: true,
    selected: true,
  });
}

function restorePlaceholderIfEmpty() {
  // Choices.js no vuelve a pintar el placeholder por si solo tras un
  // removeItem (p.ej. al pulsar la "x" de clearable) — sin esto el control
  // se queda visualmente vacio en vez de volver a mostrar el placeholder.
  if (props.multiple || !props.placeholder || !fileFormatChoices.value) return;
  const activeValue = fileFormatChoices.value.getValue(true);
  const isEmpty = Array.isArray(activeValue)
    ? activeValue.length === 0
    : isEmptyValue(activeValue);
  if (isEmpty) {
    setPlaceholderItem();
  }
}

function disposeTooltipElement(element) {
  if (!element) return;
  if (element.__tooltipInstance && typeof element.__tooltipInstance.dispose === "function") {
    element.__tooltipInstance.dispose();
  }
  element.__tooltipInstance = null;
}

function disposeSelectedTooltips() {
  const container = fileFormatChoices.value?.containerOuter?.element;
  if (!container) return;
  container.querySelectorAll(".choice-chip[data-selected-tooltip]").forEach((element) => {
    disposeTooltipElement(element);
  });
}

function syncSelectedTooltips() {
  if (!props.selectedTooltip) return;
  const container = fileFormatChoices.value?.containerOuter?.element;
  if (!container) return;
  disposeSelectedTooltips();
  container.querySelectorAll(".choice-chip[data-selected-tooltip]").forEach((element) => {
    const title = element.getAttribute("data-selected-tooltip");
    if (!title) return;
    element.__tooltipInstance = new bootstrap.Tooltip(element, {
      title,
      placement: "top",
      html: true,
      trigger: "hover",
    });
  });
}

onMounted(() => {
  if (document.getElementById(props.id)) {
    const element = document.getElementById(props.id);
    let multipleOptions = {};
    if (props.multiple) {
      multipleOptions = {
        removeItemButton: true,
      };
    }
    let formClass = `form-control form-control-${props.size}`;
    fileFormatChoices.value = new Choices(element, {
      allowHTML: true,
      position: 'auto',
      placeholder: true,
      placeholderValue: props.placeholder,
      searchEnabled: props.filterable,
      searchFields: props.choiceLabel ? ["label", "value", "customProperties.choiceLabel"] : ["label", "value"],
      searchResultLimit: 20,
      removeItemButton: true,
      shouldSort: false,
      ...multipleOptions,
      itemSelectText: "",
      noResultsText: "No resultados encontrados",
      noChoicesText: "No hay opciones para elegir",
      classNames: {
        containerInner: formClass,
      },
      callbackOnCreateTemplates: function (template) {
        const createImageHtml = (imageUrl) => {
          if (!imageUrl) return "";
          const safeUrl = encodeURI(imageUrl);
          return `<img src="${safeUrl}" alt="" style="margin-right:5px;width: 16px; height: 16px; border-radius: 50%; object-fit: cover;" />`;
        };

        const createIconHtml = (iconClass) => {
          if (!iconClass) return "";
          return `<i class="${escapeHtmlAttribute(iconClass)}" aria-hidden="true" style="margin-right:6px;width:14px;text-align:center;font-size:0.72rem;color:#64748b;"></i>`;
        };

        const createRemoveButton = (classNames, label) => {
          return `<button type="button" class="${classNames.classNames.button} choice-remove-btn"  aria-label="Remove item: '${label}'" data-button>Remove item</button>`;
        };
        return {
          // Template para los items seleccionados (chips)
          item: (classNames, data) => {
            const image = createImageHtml(data.customProperties?.image);
            const icon = !data.customProperties?.image ? createIconHtml(data.customProperties?.icon) : "";
            const label = data.label ?? "";
            const removeButtonHtml = props.multiple || props.clearable ? createRemoveButton(classNames, label) : "";
            const isPlaceholder = data.placeholder === true;
            const selectedTooltip = props.selectedTooltip
              ? String(data.customProperties?.selectedTooltip || label || "")
              : "";
            const selectedTooltipAttr = selectedTooltip
              ? `data-selected-tooltip="${escapeHtmlAttribute(selectedTooltip)}"`
              : "";
            const selectedSizeClass = `choice-chip-${props.selectedItemSize}`;
            return template(
              `<div class="${classNames.classNames.item} ${
                data.highlighted
                ? classNames.classNames.highlightedState
                : classNames.classNames.itemSelectable
              }${
                isPlaceholder ? " " + classNames.classNames.placeholder : ""
              } choice-chip ${selectedSizeClass}" data-item data-id="${data.id}" data-value="${data.value}" ${
                  props.multiple || props.clearable ? 'data-deletable' : ""
              } ${selectedTooltipAttr}>
                ${image}${icon}
                <span class="choice-label">${label}</span>
                ${removeButtonHtml}
              </div>`
            );
          },

          // Template para las opciones del dropdown
          choice: (classNames, data) => {
            const image = data.customProperties?.image ? createImageHtml(data.customProperties.image) : "";
            const icon = !data.customProperties?.image ? createIconHtml(data.customProperties?.icon) : "";
            const label = data.customProperties?.choiceLabel || data.label || "";
            const descriptionAttr = data.customProperties?.description ? `title="${data.customProperties.description}"` : "";
            return template(
              `<div class="${classNames.classNames.item} ${classNames.classNames.itemChoice} ${data.highlighted ? classNames.classNames.highlightedState : ""} ${
                data.disabled ? classNames.classNames.itemDisabled : classNames.classNames.itemSelectable
              }" data-select-text="${this.config.itemSelectText}" data-choice data-id="${data.id}" data-value="${data.value}" ${
                data.groupId > 0 ? 'role="treeitem"' : 'role="option"'
              } ${descriptionAttr}>
                ${image}${icon}
                <span class="choice-label">${label}</span>
              </div>`
            );
          },
        };
      },
      
    });
    if (!fileFormatChoices.value?.containerOuter) return;
    fileFormatChoices.value.containerOuter.element.classList.add(`choices-size-${props.size}`);
    refreshChoicesOptions(props.options, props.modelValue);
    updateValidityClass(props.error);
    syncSelectedTooltips();
    const container = fileFormatChoices.value.containerOuter.element;
    selectedTooltipCleanupHandler.value = (event) => {
      const removeButton = event.target?.closest?.(".choice-remove-btn");
      if (!removeButton) return;
      disposeTooltipElement(removeButton.closest(".choice-chip"));
    };
    container.addEventListener("mousedown", selectedTooltipCleanupHandler.value, true);
    selectedTooltipResyncHandler.value = () => {
      Promise.resolve().then(() => {
        syncSelectedTooltips();
      });
    };
    element.addEventListener("addItem", selectedTooltipResyncHandler.value);
    element.addEventListener("removeItem", selectedTooltipResyncHandler.value);
    element.addEventListener("change", selectedTooltipResyncHandler.value);
    placeholderRestoreHandler.value = () => {
      Promise.resolve().then(restorePlaceholderIfEmpty);
    };
    element.addEventListener("removeItem", placeholderRestoreHandler.value);
    const dropdownEl = fileFormatChoices.value.dropdown.element;
    dropdownEl.style.minWidth = `${props.menuWidth}px`;
    if (props.menuHeight) {
      const listEl = dropdownEl.querySelector(".choices__list");
      if (listEl) listEl.style.maxHeight = `${props.menuHeight}px`;
    }
  }
});

onBeforeUnmount(() => {
  disposeSelectedTooltips();
  const element = document.getElementById(props.id);
  const container = fileFormatChoices.value?.containerOuter?.element;
  if (container && selectedTooltipCleanupHandler.value) {
    container.removeEventListener("mousedown", selectedTooltipCleanupHandler.value, true);
  }
  if (element && selectedTooltipResyncHandler.value) {
    element.removeEventListener("addItem", selectedTooltipResyncHandler.value);
    element.removeEventListener("removeItem", selectedTooltipResyncHandler.value);
    element.removeEventListener("change", selectedTooltipResyncHandler.value);
  }
  if (element && placeholderRestoreHandler.value) {
    element.removeEventListener("removeItem", placeholderRestoreHandler.value);
  }
  selectedTooltipCleanupHandler.value = null;
  selectedTooltipResyncHandler.value = null;
  placeholderRestoreHandler.value = null;
});

const updateValidityClass = (error) => {
  const container = fileFormatChoices.value?.containerOuter.element;

  if (error) {
    container.classList.add("is-invalid");
    container.classList.remove("is-valid");
  } else {
    container.classList.add("is-valid");
    container.classList.remove("is-invalid");
  }
};
const loadValue = (newValue, options = props.options) => {
  if (!fileFormatChoices.value) {
    if (!areValuesEqual(localValue.value, newValue)) {
      startProgrammaticSync();
      localValue.value = newValue;
    }
    return;
  }

  startProgrammaticSync();
  fileFormatChoices.value.removeActiveItems();

  if (Array.isArray(newValue) && props.multiple) {
    newValue.forEach((value) => {
      const selected = options.find((option) => {
        return option[props.nameValue] == value;
      });
      if (selected) {
        fileFormatChoices.value.setChoiceByValue(selected[props.nameValue]);
      }
    });
  } else {
    const selected = options.find((option) => {
      return option[props.nameValue] == newValue;
    });
    if (selected) {
      fileFormatChoices.value.setChoiceByValue(selected[props.nameValue]);
    } else if (isEmptyValue(newValue)) {
      setPlaceholderItem();
    }
  }
  if (!areValuesEqual(localValue.value, newValue)) {
    localValue.value = newValue;
  }
};

const refreshChoicesOptions = (options = props.options, modelValue = props.modelValue) => {
  if (!fileFormatChoices.value) return;

  const normalizedValue = getNormalizedValue(modelValue, options);
  const formattedOptions = formatOptions(options);
  const nextOptionsSignature = buildOptionsSignature(options);

  if (nextOptionsSignature === optionsSignature.value && areValuesEqual(localValue.value, normalizedValue)) {
    return;
  }

  optionsSignature.value = nextOptionsSignature;

  startProgrammaticSync();
  fileFormatChoices.value.clearStore();
  fileFormatChoices.value.setChoices(formattedOptions, "value", "label", false);
  loadValue(normalizedValue, options);
};

const getClasses = (size, success, error) => {
  let sizeValue = size ? `form-select-${size}` : null;
  let isValidValue = error ? "is-invalid" : success ? "is-valid" : "";
  return `${sizeValue} ${isValidValue}`;
};

watch(
  () => localValue.value,
  (value) => {
    if (isSyncingFromProps.value) return;
    if (areValuesEqual(value, props.modelValue)) return;
    emit("update:modelValue", value);
  }
);

watch(
  () => props.error,
  (newVal) => {
    if (fileFormatChoices.value) {
      updateValidityClass(newVal);
    }
  },
  { immediate: true }
);
watch(
  () => props.modelValue,
  (newVal) => {
    if (fileFormatChoices.value) {
      const normalizedValue = getNormalizedValue(newVal, props.options);
      if (areValuesEqual(localValue.value, normalizedValue)) return;
      loadValue(normalizedValue, props.options);
    }
  }
);

watch(
  () => props.options,
  (newOptions) => {
  if (fileFormatChoices.value) {
      refreshChoicesOptions(newOptions, props.modelValue);
    }
  },
  { deep: true }
);
</script>
<template>
  <div class="form-group">
    <p
      v-if="label != ''"
      class="fs-6 fw-bold"
      :class="{ required: isRequired }"
    >
      {{ label }}
    </p>
    <select
      :id="id"
      v-model="localValue"
      :name="name"
      :required="isRequired"
      class="form-select"
      :class="getClasses(size, success, error)"
      :disabled="disabled"
      :multiple="multiple"
      @change="emit('selectedChange', localValue)"
    ></select>
  </div>
</template>
<style scoped>
:deep(.choice-chip.choice-chip-sm) {
  padding: 0.05rem 0.22rem !important;
  min-height: auto;
  border-radius: 0.24rem;
}

:deep(.choice-chip.choice-chip-sm .choice-label) {
  font-size: 0.52rem;
  line-height: 1;
}

:deep(.choice-chip.choice-chip-sm .choice-remove-btn) {
  transform: scale(0.82);
  transform-origin: center;
}

:deep(.form-control-xs) {
  border-radius: 0.5rem;
  font-size: 0.64rem;
  min-height: 26px;
  padding: 0.08rem 0.4rem;
}

:deep(.choices[data-type*='select-one'] .form-control-xs) {
  padding-bottom: 0.08rem;
  padding-right: 1.35rem;
  padding-top: 0.08rem;
}

:deep(.form-control-xs .choices__item) {
  font-size: 0.64rem;
}

:deep(.choices[data-type*='select-one'] .form-control-xs .choices__item) {
  line-height: 1.2;
}

:deep(.choices.choices-size-xs[data-type*='select-one']::after) {
  font-size: 12px;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

:deep(.choices.choices-size-xs[data-type*='select-one'].is-open::after) {
  transform: translateY(-50%) rotate(180deg);
}

:deep(.choices.choices-size-xs .choices__list--dropdown) {
  padding: 0.2rem;
}

:deep(.choices.choices-size-xs .choices__list--dropdown .choices__item) {
  font-size: 0.70rem;
  line-height: 1.2;
  padding: 0.3rem 0.45rem;
}

:deep(.choice-chip.choice-chip-xs) {
  border-radius: 0.22rem;
  min-height: auto;
  padding: 0.02rem 0.18rem !important;
}

:deep(.choice-chip.choice-chip-xs .choice-label) {
  font-size: 0.46rem;
  line-height: 1;
}

:deep(.choice-chip.choice-chip-xs .choice-remove-btn) {
  transform: scale(0.72);
  transform-origin: center;
}
</style>
